import React, { useEffect, useState, memo, createElement, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Lock, Heart, Sparkles, Flame, MessageCircle, ChevronDown, ShieldCheck, Zap, EyeOff, Smartphone, Users, PartyPopper, Gift, CalendarHeart, Infinity as InfinityIcon, Unlock, RefreshCw, CreditCard, Layers, Dices } from 'lucide-react';
// --- Constants & Assets ---
const CHECKOUT_URL = 'https://origenlabonline.shop/cart/46317834797228:1';
const VIDEO_URL = 'https://cdn.magicpatterns.com/uploads/cEu1TvHBpr39hS8SPh2etf/oksy04210307_pindown.io_1780105522.mp4';
const APP_MOCKUP_URL = 'https://cdn.magicpatterns.com/uploads/whmdnU8pKp2GtsQuGBsNM3/ChatGPT_Image_28_may_2026,_11_26_36_a.m..png';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/gsjCZQ89Mqk9Uz8MVVQDwE/WhatsApp_Image_2026-05-29_at_20.27.51.jpg';
const MP_LOGO_URL = 'https://cdn.magicpatterns.com/uploads/h9zBi5cHNRMB7BVWkHXyKr/descarga_(2).png';
// --- Reusable Components ---
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 20
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-0" data-mp="3mkhlbqxmcy4g">
    {children}
  </motion.div>;
const CTAButton = ({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) => <a href={CHECKOUT_URL} className={`group relative inline-flex items-center justify-center px-8 py-4 text-sm md:text-base font-bold text-white uppercase tracking-widest bg-[#FF2D7A] rounded-full transition-all hover:scale-105 shadow-[0_0_20px_rgba(255,45,122,0.4)] hover:shadow-[0_0_30px_rgba(255,45,122,0.6)] w-full sm:w-auto ${className}`} data-id="element-1" data-mp="33mouvo46mgny">
    <span className="relative flex items-center gap-2" data-id="element-2" data-mp="nuhjilyq8hvv">{children}</span>
  </a>;
// --- Sections ---
const HeroSection = () => <section className="relative min-h-[90svh] flex items-center justify-center overflow-hidden pt-20 pb-10" data-id="element-3" data-mp="sssqmoxn0eae">
    {/* Background Video - Increased visibility */}
    <div className="absolute inset-0 w-full h-full" data-id="element-4" data-mp="3udkmfuysas9v">
      <video autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover opacity-75" data-id="element-5" data-mp="3uevuw836co7b">
        <source src={VIDEO_URL} type="video/mp4" data-id="element-6" data-mp="85l1usekr09q" />
      </video>
      {/* Lighter gradients to make the couple pop more */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/20 to-black/95" data-id="element-7" data-mp="3gkuxpirvf992" />
    </div>

    {/* Header/Logo */}
    <div className="absolute top-0 left-0 w-full p-4 flex justify-center z-20" data-id="element-8" data-mp="3bnutzeihuil4">
      <img src={LOGO_URL} alt="El Juego del Deseo" className="h-10 md:h-14 rounded-full opacity-90" data-id="element-9" data-mp="3qc95sexw1hdt" />
    </div>

    {/* Content */}
    <div className="relative z-10 container mx-auto px-4 flex flex-col items-center text-center mt-8" data-id="element-10" data-mp="3lj6bg4lae3wk">
      <FadeIn data-id="element-11" data-mp="2xssh27f8bsyt">
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-4 text-white text-glow" data-id="element-12" data-mp="2xsk64l2o2xq9">
          ESTA NOCHE PUEDE
          <br data-id="element-13" data-mp="18dhopv1l8k1s" />
          <span className="text-[#FF2D7A]" data-id="element-14" data-mp="18cznsl3zdhnj">SER DISTINTA.</span>
        </h1>
      </FadeIn>

      <FadeIn delay={0.1} data-id="element-15" data-mp="1d5j9y6gqhl3y">
        <p className="text-lg md:text-xl text-zinc-200 max-w-2xl mx-auto mb-8 font-light leading-relaxed drop-shadow-md" data-id="element-16" data-mp="1d5ssj8zg8pzm">
          Preguntas, retos y experiencias diseñadas para parejas que quieren
          salir de la rutina y volver a sorprenderse.
        </p>
      </FadeIn>

      <FadeIn delay={0.2} className="w-full max-w-md mx-auto mb-8" data-id="element-17" data-mp="1r5kafkt36qiw">
        <ul className="flex flex-col gap-3" data-id="element-18" data-mp="1w3vk9vjdt114">
          {[{
          text: 'Más conexión',
          icon: Heart
        }, {
          text: 'Más diversión',
          icon: Sparkles
        }, {
          text: 'Más complicidad',
          icon: Flame
        }, {
          text: '100% privado',
          icon: Lock
        }].map((item, i) => <li key={i} className="flex items-center gap-3 text-white bg-black/40 backdrop-blur-md py-3 px-5 rounded-xl border border-white/10" data-id="element-19" data-mp="6mukh1m9kgin" data-mpi={i}>
              <item.icon className="w-5 h-5 text-[#FF2D7A]" data-id="element-20" data-mp="qwvpk8lpwd3q" />
              <span className="font-medium tracking-wide" data-id="element-21" data-mp="qyglo2nok43p">{item.text}</span>
            </li>)}
        </ul>
      </FadeIn>

      <FadeIn delay={0.3} className="flex flex-col items-center w-full max-w-md" data-id="element-22" data-mp="m4m16x093l3k">
        <CTAButton className="mb-4" data-id="element-23" data-mp="h7m070i9d8jb">DESBLOQUEAR MI EXPERIENCIA</CTAButton>

        <p className="text-sm text-zinc-300 mb-3 tracking-wide font-light drop-shadow-md" data-id="element-24" data-mp="c9kcg29w0l1i">
          Acceso completo por{' '}
          <span className="text-white font-bold" data-id="element-25" data-mp="19tx70swgk5ok">$16.990</span> · Pago único
        </p>

        <div className="flex items-center gap-2 text-xs text-zinc-300 tracking-wider uppercase bg-black/50 px-4 py-2 rounded-full backdrop-blur-sm" data-id="element-26" data-mp="14xpz4uki4k3z">
          <Lock className="w-3 h-3 text-[#FF2D7A]" data-id="element-27" data-mp="vwrt66qi62ir" />
          <span data-id="element-28" data-mp="vvyxc0t30xfe">Pago seguro con Mercado Pago</span>
        </div>
      </FadeIn>
    </div>
  </section>;
const IncludesSection = () => {
  const items = [{
    title: 'Ruleta de la Pasión',
    icon: Flame
  }, {
    title: 'Cartas del Deseo',
    icon: Layers
  }, {
    title: 'Verdad o Atrevimiento',
    icon: MessageCircle
  }, {
    title: 'Desafíos para parejas',
    icon: Dices
  }, {
    title: 'Experiencias personalizadas',
    icon: Sparkles
  }, {
    title: 'Más de 1.000 combinaciones',
    icon: InfinityIcon
  }];
  return <section className="py-16 md:py-20 bg-black relative overflow-hidden" data-id="element-29" data-mp="29vjur2ap4xmb">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#FF2D7A]/5 rounded-full blur-[120px] pointer-events-none" data-id="element-30" data-mp="29xwixx71s47t" />

      <div className="container mx-auto px-4" data-id="element-31" data-mp="2jsogx24jkt34">
        <div className="flex flex-col lg:flex-row items-center gap-10" data-id="element-32" data-mp="2nvl65bol7ng1">
          <FadeIn className="w-full lg:w-1/2 flex justify-center order-2 lg:order-1" data-id="element-33" data-mp="2nwe87zclporu">
            <img src={APP_MOCKUP_URL} alt="App Interface" className="max-w-full h-auto w-[280px] md:w-[350px] drop-shadow-[0_0_30px_rgba(255,45,122,0.15)]" data-id="element-34" data-mp="2su5y6ntor0te" />
          </FadeIn>

          <div className="w-full lg:w-1/2 order-1 lg:order-2" data-id="element-35" data-mp="yhprjlwz9ko0">
            <FadeIn data-id="element-36" data-mp="21raonxbt5r1u">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 leading-tight" data-id="element-37" data-mp="21qskmeiwm8jd">
                ¿QUÉ INCLUYE <span className="text-[#FF2D7A]" data-id="element-38" data-mp="1wt0w7uhgx4jx">EL JUEGO?</span>
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-39" data-mp="2as0iwwlf79dg">
                {items.map((item, i) => <div key={i} className="flex items-center gap-3 bg-white/[0.03] border border-white/5 p-4 rounded-xl" data-id="element-40" data-mp="25tfe0jv841io" data-mpi={i}>
                    <item.icon className="w-6 h-6 text-[#FF2D7A] flex-shrink-0" data-id="element-41" data-mp="2kpn23v9r5n38" />
                    <span className="text-zinc-200 font-light" data-id="element-42" data-mp="2frv8mwtglswx">
                      {item.title}
                    </span>
                  </div>)}
              </div>

              <CTAButton data-id="element-43" data-mp="2tkkvf3gtb1v3">QUIERO ACCEDER</CTAButton>
            </FadeIn>
          </div>
        </div>
      </div>
    </section>;
};
const WhyItWorksSection = () => {
  const benefits = ['Rompe el hielo rápidamente', 'Genera conversaciones inesperadas', 'Crea momentos memorables', 'Se adapta al nivel de intensidad que elijan', 'Cada experiencia es diferente', 'Miles de combinaciones posibles', 'No se vuelve repetitivo'];
  return <section className="py-16 md:py-20 bg-[#030303] border-y border-white/5" data-id="element-44" data-mp="3n2fyzmqln3s6">
      <div className="container mx-auto px-4 max-w-4xl" data-id="element-45" data-mp="3n0dbb9cx5mqm">
        <FadeIn className="text-center mb-10" data-id="element-46" data-mp="384nmz4k9gxiq">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight" data-id="element-47" data-mp="3d4jp0y9eeevn">
            ¿POR QUÉ <span className="text-[#FF2D7A]" data-id="element-48" data-mp="3d6mnm6om9gbz">FUNCIONA?</span>
          </h2>
        </FadeIn>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10" data-id="element-49" data-mp="2zb27fj09vc89">
          {benefits.map((benefit, i) => <FadeIn key={i} delay={i * 0.05} className="flex items-start gap-3" data-id="element-50" data-mp="345yarfaylm0o" data-mpi={i}>
              <div className="mt-1 bg-[#FF2D7A]/10 p-1 rounded-full flex-shrink-0" data-id="element-51" data-mp="2pgioabigo2qs">
                <Check className="w-4 h-4 text-[#FF2D7A]" data-id="element-52" data-mp="2uetwkht3y56w" />
              </div>
              <span className="text-zinc-300 font-light text-lg" data-id="element-53" data-mp="2ge0zbpbf8w03">
                {benefit}
              </span>
            </FadeIn>)}
        </div>

        <FadeIn className="flex justify-center" data-id="element-54" data-mp="2khz004geaev9">
          <CTAButton data-id="element-55" data-mp="30vnc02o1jgcx">EMPEZAR AHORA</CTAButton>
        </FadeIn>
      </div>
    </section>;
};
const WhoIsItForSection = () => {
  const audiences = [{
    text: 'Parejas nuevas que quieren conocerse mejor',
    icon: Heart
  }, {
    text: 'Parejas de años que quieren salir de la rutina',
    icon: RefreshCw
  }, {
    text: 'Citas románticas',
    icon: Flame
  }, {
    text: 'Aniversarios y ocasiones especiales',
    icon: CalendarHeart
  }, {
    text: 'Personas que quieren sorprender a su pareja',
    icon: Gift
  }, {
    text: 'Grupos de amigos que buscan diversión',
    icon: Users
  }, {
    text: 'Reuniones privadas y fiestas',
    icon: PartyPopper
  }, {
    text: 'Cualquiera que quiera una experiencia diferente',
    icon: Sparkles
  }];
  return <section className="py-16 md:py-20 bg-black" data-id="element-56" data-mp="235sbjmytkpio">
      <div className="container mx-auto px-4" data-id="element-57" data-mp="1y7h41ivzyr3m">
        <FadeIn className="text-center mb-12" data-id="element-58" data-mp="1yaczn0kdh3ow">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" data-id="element-59" data-mp="1u7zvuhp6rfur">
            JUGÁ COMO <span className="text-[#FF2D7A]" data-id="element-60" data-mp="9po0cvmlerzm">QUIERAS</span>
          </h2>
          <p className="text-xl text-zinc-400 font-light" data-id="element-61" data-mp="dqhmpkkdzyqe">
            No es solo para parejas. El Juego del Deseo incluye experiencias
            para todos.
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-6xl mx-auto" data-id="element-62" data-mp="inridhydd6mo">
          {audiences.map((aud, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-63" data-mp="3m6t8tmn9t0s1" data-mpi={i}>
              <div className="bg-white/[0.02] border border-white/5 p-6 rounded-2xl h-full flex flex-col items-center text-center hover:border-[#FF2D7A]/30 transition-colors" data-id="element-64" data-mp="3m84drpsgcekq">
                <aud.icon className="w-8 h-8 text-[#FF2D7A] mb-4 opacity-80" data-id="element-65" data-mp="3w3p8d2v6yckg" />
                <span className="text-zinc-300 font-light text-sm leading-relaxed" data-id="element-66" data-mp="4q310k3ighmd">
                  {aud.text}
                </span>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
};
const InsideLookSection = () => <section className="bg-black w-full py-16 md:py-24" data-id="element-67" data-mp="38ctl8j1ok5jj">
    <div className="container mx-auto px-4 flex justify-center" data-id="element-68" data-mp="2f7wrl87ya4ly">
      <FadeIn className="w-full max-w-[1400px]" data-id="element-69" data-mp="2k6811huc2d3n">
        <img src="https://cdn.magicpatterns.com/uploads/iF7pFki2dNmR2c9oe9Wi4f/ChatGPT_Image_22_jul_2026,_01_02_39_p.m..png" alt="Así es la app por dentro" className="w-full h-auto object-contain block" data-id="element-70" data-mp="2k4wy1k8pp9dl" />
      </FadeIn>
    </div>
  </section>;
const ValueStackSection = () => {
  const items = ['Acceso inmediato', 'Todas las modalidades desbloqueadas', 'Actualizaciones futuras', 'Compatible con cualquier celular', 'Sin descargas', 'Uso ilimitado', 'Experiencias para todos los niveles'];
  return <section className="py-16 md:py-20 bg-[#030303] border-t border-white/5" data-id="element-71" data-mp="26cyx3wp5mjsi">
      <div className="container mx-auto px-4 max-w-3xl" data-id="element-72" data-mp="2b6uqsmydxkhv">
        <FadeIn className="bg-gradient-to-b from-white/[0.05] to-transparent border border-white/10 rounded-3xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-73" data-mp="1dqxq0jlsx865">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-1 bg-gradient-to-r from-transparent via-[#FF2D7A] to-transparent opacity-50" data-id="element-74" data-mp="1imw8jqkg7vyn" />

          <h2 className="text-2xl md:text-3xl font-bold mb-8" data-id="element-75" data-mp="3rz8b8kg4917f">
            TODO LO QUE <span className="text-[#FF2D7A]" data-id="element-76" data-mp="3my150ptmga2v">RECIBÍS</span>
          </h2>

          <div className="flex flex-col gap-4 mb-10 text-left max-w-md mx-auto" data-id="element-77" data-mp="3hzpu0brlbtj2">
            {items.map((item, i) => <div key={i} className="flex items-center gap-4 border-b border-white/5 pb-4 last:border-0 last:pb-0" data-id="element-78" data-mp="3d0eboybksfzd" data-mpi={i}>
                <div className="bg-[#FF2D7A]/20 p-1.5 rounded-full" data-id="element-79" data-mp="389o55hrey733">
                  <Check className="w-5 h-5 text-[#FF2D7A]" data-id="element-80" data-mp="348cd75l2h9rq" />
                </div>
                <span className="text-zinc-200 font-light text-lg" data-id="element-81" data-mp="3468d6yqaguwq">{item}</span>
              </div>)}
          </div>

          <CTAButton data-id="element-82" data-mp="2ua59hofsgo7o">DESBLOQUEAR TODO AHORA</CTAButton>
        </FadeIn>
      </div>
    </section>;
};
const SocialProofSection = () => {
  const testimonials = [{
    id: 1,
    user: 'martina.g',
    text: 'Lo compramos para una noche tranquila y terminamos jugando hasta las 3 AM.',
    likes: 124,
    avatar: 'https://i.pravatar.cc/150?img=47'
  }, {
    id: 2,
    user: 'juanpablo_88',
    text: 'Pensé que era otro juego más, pero nos sorprendió muchísimo.',
    likes: 89,
    avatar: 'https://i.pravatar.cc/150?img=11'
  }, {
    id: 3,
    user: 'cami_y_leo',
    text: 'Nos hizo hablar de cosas que nunca habían salido.',
    likes: 256,
    avatar: 'https://i.pravatar.cc/150?img=32'
  }, {
    id: 4,
    user: 'sofi.mendez',
    text: 'La ruleta fue lo mejor.',
    likes: 45,
    avatar: 'https://i.pravatar.cc/150?img=5'
  }, {
    id: 5,
    user: 'nico_ferrari',
    text: 'Lo llevamos a una reunión con amigos y fue un éxito.',
    likes: 112,
    avatar: 'https://i.pravatar.cc/150?img=12'
  }, {
    id: 6,
    user: 'valen.ruiz',
    text: 'Cada ronda era distinta.',
    likes: 67,
    avatar: 'https://i.pravatar.cc/150?img=44'
  }];
  return <section className="py-16 bg-black border-t border-white/5 overflow-hidden" data-id="element-83" data-mp="3m53451bkbwdf">
      <div className="container mx-auto px-4 max-w-5xl" data-id="element-84" data-mp="1anhu3md4ylwi">
        <FadeIn className="text-center mb-10" data-id="element-85" data-mp="15pg92vw5gcie">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight" data-id="element-86" data-mp="15ooqqmrkcba2">
            LO QUE DICEN{' '}
            <span className="text-[#FF2D7A]" data-id="element-87" data-mp="1kdt8k2ug4dhn">QUIENES YA JUGARON</span>
          </h2>
        </FadeIn>

        <div className="flex overflow-x-auto snap-x snap-mandatory gap-4 pb-8 -mx-4 px-4 md:mx-0 md:px-0 md:grid md:grid-cols-2 lg:grid-cols-3 md:overflow-visible md:snap-none hide-scrollbar" data-id="element-88" data-mp="1ffrr1mco4m8a">
          {testimonials.map((t, i) => <FadeIn key={t.id} delay={i * 0.05} className="min-w-[280px] sm:min-w-[320px] md:min-w-0 snap-center flex-shrink-0" data-id="element-89" data-mp="msowf5b44vnb" data-mpi={i}>
              <div className="bg-[#0a0a0a] border border-zinc-800/60 p-5 rounded-2xl flex gap-3 h-full" data-id="element-90" data-mp="wp1ltqzmknc9">
                <img src={t.avatar} alt={t.user} className="w-10 h-10 rounded-full object-cover flex-shrink-0 border border-zinc-800" data-id="element-91" data-mp="2d2ic08iz4uwk" />
                <div className="flex-1" data-id="element-92" data-mp="283f6kmz30mo2">
                  <p className="text-sm font-semibold text-zinc-100" data-id="element-93" data-mp="hmtfjfxv58cp">
                    {t.user}
                  </p>
                  <p className="text-sm text-zinc-300 mt-1 leading-snug font-light" data-id="element-94" data-mp="mlegxioupy2q">
                    {t.text}
                  </p>
                  <div className="flex items-center gap-4 mt-3" data-id="element-95" data-mp="qlycwixouvna">
                    <span className="text-xs text-zinc-500 font-medium" data-id="element-96" data-mp="vn494qqk511k">
                      {t.likes} Me gusta
                    </span>
                    <span className="text-xs text-zinc-600 font-medium cursor-pointer" data-id="element-97" data-mp="10lffuslk2vfk">
                      Responder
                    </span>
                  </div>
                </div>
                <div className="flex-shrink-0 pt-1" data-id="element-98" data-mp="1ac0pdb2rpkd9">
                  <Heart className="w-4 h-4 text-zinc-500 hover:text-[#FF2D7A] transition-colors cursor-pointer" data-id="element-99" data-mp="1ecu8zs96gd0c" />
                </div>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
};
const FAQSection = () => {
  const faqs = [{
    q: '¿Funciona en celular?',
    a: 'Sí, está optimizado para dispositivos móviles.'
  }, {
    q: '¿Necesito descargar algo?',
    a: 'No. Accedés directamente desde el navegador.'
  }, {
    q: '¿Es una suscripción?',
    a: 'No. Es un pago único.'
  }, {
    q: '¿Recibo acceso inmediatamente?',
    a: 'Sí, automáticamente luego de la compra.'
  }];
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <section className="py-16 bg-[#030303] border-t border-white/5" data-id="element-100" data-mp="2h5onn3lkkugr">
      <div className="container mx-auto px-4 max-w-3xl" data-id="element-101" data-mp="2h4wr9qk5espf">
        <FadeIn className="text-center mb-10" data-id="element-102" data-mp="2c6bslvkjoh33">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight" data-id="element-103" data-mp="2c8oicuwjnvqp">
            PREGUNTAS FRECUENTES
          </h2>
        </FadeIn>

        <div className="space-y-2" data-id="element-104" data-mp="23eterrb2mdd6">
          {faqs.map((faq, i) => <FadeIn key={i} delay={i * 0.05} data-id="element-105" data-mp="23c74vl0pjlrs" data-mpi={i}>
              <div className={`border-b border-white/10 overflow-hidden transition-colors ${openIndex === i ? 'bg-white/[0.02]' : 'bg-transparent'}`} data-id="element-106" data-mp="2zwfu9xe6a6rg">
                <button className="w-full px-4 py-5 text-left flex justify-between items-center focus:outline-none" onClick={() => setOpenIndex(openIndex === i ? null : i)} data-id="element-107" data-mp="2v3d0pp9eaxb9">
                  <span className="font-light text-zinc-200" data-id="element-108" data-mp="y6h0wuaurnko">{faq.q}</span>
                  <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform duration-300 ${openIndex === i ? 'rotate-180 text-[#FF2D7A]' : ''}`} data-id="element-109" data-mp="jivy71hw3qda" />
                </button>
                <AnimatePresence data-id="element-110" data-mp="ahpi2tj5khkk">
                  {openIndex === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-111" data-mp="ag4lkyda2ok2">
                      <div className="px-4 pb-5 text-zinc-400 font-light text-sm" data-id="element-112" data-mp="1uxj14yw5gsk8">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </FadeIn>)}
        </div>
      </div>
    </section>;
};
const TrustAndPricingSection = () => <section className="py-20 relative overflow-hidden bg-black" data-id="element-113" data-mp="3fgczw1jtouws">
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,45,122,0.08)_0%,rgba(0,0,0,1)_70%)]" data-id="element-114" data-mp="3u47u5mdhb7bz" />
    <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#FF2D7A]/30 to-transparent" data-id="element-115" data-mp="3pd7x8h4d0p9h" />

    <div className="container mx-auto px-4 relative z-10 text-center max-w-3xl" data-id="element-116" data-mp="6y72lcvgzf49">
      <FadeIn data-id="element-117" data-mp="2yomcuelfs7r">
        {/* Trust Badges */}
        <div className="flex flex-col items-center mb-10" data-id="element-118" data-mp="2zzl2ft6m9s0">
          <img src={MP_LOGO_URL} alt="Mercado Pago" className="h-12 object-contain mb-6 opacity-90" data-id="element-119" data-mp="gwmx7ptph6ii" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-zinc-300 font-light text-left bg-white/[0.03] p-6 rounded-2xl border border-white/10 w-full max-w-lg mx-auto" data-id="element-120" data-mp="bzmmh1lwvfl2">
            <div className="flex items-center gap-2" data-id="element-121" data-mp="2dqrc5s4aa62r">
              <ShieldCheck className="w-4 h-4 text-[#FF2D7A]" data-id="element-122" data-mp="2dsaxvy8pl9bx" /> Pago seguro con
              Mercado Pago
            </div>
            <div className="flex items-center gap-2" data-id="element-123" data-mp="1wvltfun7gpeh">
              <CreditCard className="w-4 h-4 text-[#FF2D7A]" data-id="element-124" data-mp="1wu2h2f4o6yhz" /> Tarjetas de
              crédito y débito
            </div>
            <div className="flex items-center gap-2" data-id="element-125" data-mp="21oya9iln7rwo">
              <RefreshCw className="w-4 h-4 text-[#FF2D7A]" data-id="element-126" data-mp="26kdl01oct7ov" /> Posibilidad de
              pago en cuotas
            </div>
            <div className="flex items-center gap-2" data-id="element-127" data-mp="18zrcwmxxd8d0">
              <Zap className="w-4 h-4 text-[#FF2D7A]" data-id="element-128" data-mp="1dw7zhcph7enz" /> Acceso inmediato al
              comprar
            </div>
            <div className="flex items-center gap-2 sm:col-span-2" data-id="element-129" data-mp="1ivb3quxmtguw">
              <Unlock className="w-4 h-4 text-[#FF2D7A]" data-id="element-130" data-mp="1nvqf5gycsea8" /> Sin suscripciones
              mensuales
            </div>
          </div>
        </div>

        <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight" data-id="element-131" data-mp="2uarhc4dz1sp9">
          DESCUBRÍ QUÉ EXPERIENCIA <br data-id="element-132" data-mp="2z8j5cnbhwuo6" />
          <span className="text-[#FF2D7A]" data-id="element-133" data-mp="2zcgnp6q91i70">ENCAJA CON USTEDES</span>
        </h2>

        <div className="mb-6" data-id="element-134" data-mp="nm7j2okgg1np">
          <span className="text-6xl md:text-7xl font-bold text-white text-glow tracking-tighter" data-id="element-135" data-mp="inxl667cimye">
            $16.990
          </span>
        </div>

        <p className="text-xl text-[#FF2D7A] font-medium mb-6" data-id="element-136" data-mp="dvwd6wh7nie6">
          Acceso completo por única vez
        </p>

        <div className="flex flex-col gap-4 text-zinc-300 text-left max-w-sm mx-auto mb-10" data-id="element-137" data-mp="8xukrkxwa6q1">
          <div className="flex items-center gap-3" data-id="element-138" data-mp="40kxaau2g54u">
            <span className="text-xl leading-none" data-id="element-139" data-mp="3w2ariwje5ud3">💳</span>
            <span className="font-light" data-id="element-140" data-mp="3w4wx4qmq1zso">
              Hasta 3 cuotas con Mercado Pago sin interes
            </span>
          </div>
          <div className="flex items-center gap-3" data-id="element-141" data-mp="3m4wb2wxgx5ks">
            <span className="text-xl leading-none" data-id="element-142" data-mp="3m6h58lfvemi4">⚡</span>
            <span className="font-light" data-id="element-143" data-mp="1pcmec16s1k0x">
              Acceso inmediato después de la compra
            </span>
          </div>
          <div className="flex items-center gap-3" data-id="element-144" data-mp="1kdsyw37xjiy4">
            <span className="text-xl leading-none" data-id="element-145" data-mp="2c72qyosezz48">🔒</span>
            <span className="font-light" data-id="element-146" data-mp="2c6km53rosckp">Pago 100% seguro</span>
          </div>
          <div className="flex items-center gap-3" data-id="element-147" data-mp="279kewpj3p3s0">
            <span className="text-xl leading-none" data-id="element-148" data-mp="22hj5dbdbhr5o">👥</span>
            <span className="font-light" data-id="element-149" data-mp="22fq0a0icrezs">
              Se puede jugar en pareja o en grupos de amigos
            </span>
          </div>
        </div>

        <CTAButton className="w-full sm:w-auto px-12 py-5 text-lg mb-8" data-id="element-150" data-mp="1the1hb7c1aw9">
          QUIERO ACCEDER AHORA
        </CTAButton>
      </FadeIn>
    </div>
  </section>;
const Footer = () => <footer className="py-8 bg-black border-t border-white/5 text-center text-zinc-600 text-sm font-light" data-id="element-151" data-mp="1jiosy91bt2so">
    <p data-id="element-152" data-mp="1ejlq8v8tj8nv">
      © {new Date().getFullYear()} El Juego del Deseo. Todos los derechos
      reservados.
    </p>
  </footer>;
export function App() {
  useEffect(() => {
    const f = window;
    const b = document;
    const e = 'script';
    const v = 'https://connect.facebook.net/en_US/fbevents.js';
    if (f.fbq) return;
    const n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    const t = b.createElement(e) as HTMLScriptElement;
    t.async = !0;
    t.src = v;
    const s = b.getElementsByTagName(e)[0];
    if (s && s.parentNode) {
      s.parentNode.insertBefore(t, s);
    } else {
      b.head.appendChild(t);
    }
    f.fbq('init', '2418833905303854');
    f.fbq('track', 'PageView');
  }, []);
  return <div className="min-h-screen bg-black text-white selection:bg-[#FF2D7A]/30" data-id="element-153" data-mp="32j66cmgkgfld">
      <HeroSection data-id="element-154" data-mp="37f4ss4ic3nj5" />
      <IncludesSection data-id="element-155" data-mp="3cfiznj878rfl" />
      <InsideLookSection data-id="element-156" data-mp="3cg28mafvg5hh" />
      <ValueStackSection data-id="element-157" data-mp="3cgbw9rdg20k0" />
      <WhyItWorksSection data-id="element-158" data-mp="3cdfyq45iddw3" />
      <WhoIsItForSection data-id="element-159" data-mp="mqh0as43kanh" />
      <SocialProofSection data-id="element-160" data-mp="mr0a1ljlfsqf" />
      <FAQSection data-id="element-161" data-mp="mpp1l8f7dwsz" />
      <TrustAndPricingSection data-id="element-162" data-mp="msbdfk94myh0" />
      <Footer data-id="element-163" data-mp="ros9r1qhcj56" />
      <noscript data-id="element-164" data-mp="roifh1yejpto">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=2418833905303854&ev=PageView&noscript=1" alt="" data-id="element-165" data-mp="ro0bfj5i07b7" />
      </noscript>
    </div>;
}